import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  FileText, 
  Brain, 
  Database,
  Loader2,
  X
} from 'lucide-react';
import io from 'socket.io-client';

const ResumeParsingProgress = ({ jobId, onComplete, onError, onClose }) => {
  const [job, setJob] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    console.log('🔌 FRONTEND - Initializing Socket.IO connection for job:', jobId);
    console.log('🔌 FRONTEND - API URL:', process.env.REACT_APP_API_URL);
    
    // Fallback: Fetch job status via HTTP if Socket.IO fails
    const fetchJobStatus = async () => {
      try {
        console.log('🔌 FRONTEND - Fetching job status via HTTP fallback');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload/progress/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('🔌 FRONTEND - HTTP job status received:', data.data);
          setJob(data.data);
        }
      } catch (error) {
        console.log('🔌 FRONTEND - HTTP fallback failed:', error);
      }
    };
    
    // Try HTTP fallback immediately
    fetchJobStatus();
    
    // Initialize Socket.IO connection
    const newSocket = io(process.env.REACT_APP_API_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('🔌 FRONTEND - Connected to server for progress tracking');
      console.log('🔌 FRONTEND - Socket ID:', newSocket.id);
      setIsConnected(true);
      
      // Subscribe to progress updates for this job
      const userId = localStorage.getItem('userId');
      console.log('🔌 FRONTEND - Subscribing to progress for job:', jobId, 'user:', userId);
      newSocket.emit('subscribe-progress', { 
        jobId, 
        userId: userId
      });
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Disconnected from server');
      setIsConnected(false);
    });

    newSocket.on('progress-update', (jobData) => {
      console.log('📊 FRONTEND - Progress update received:', jobData);
      console.log('📊 FRONTEND - Job status:', jobData.status);
      console.log('📊 FRONTEND - Job progress:', jobData.progress);
      console.log('📊 FRONTEND - Current step:', jobData.currentStep);
      setJob(jobData);
    });

    newSocket.on('job-completed', (jobData) => {
      console.log('✅ FRONTEND - Job completed:', jobData);
      setJob(jobData);
      if (onComplete) {
        onComplete(jobData);
      }
    });

    newSocket.on('job-failed', (jobData) => {
      console.log('❌ FRONTEND - Job failed:', jobData);
      setJob(jobData);
      if (onError) {
        onError(jobData);
      }
    });

    setSocket(newSocket);

    // Polling fallback - check job status every 2 seconds
    const pollInterval = setInterval(async () => {
      if (!job || job.status === 'completed' || job.status === 'failed') {
        clearInterval(pollInterval);
        return;
      }
      
      try {
        console.log('🔌 FRONTEND - Polling job status...');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload/progress/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('🔌 FRONTEND - Polled job status:', data.data);
          setJob(data.data);
          
          if (data.data.status === 'completed' && onComplete) {
            onComplete(data.data);
          } else if (data.data.status === 'failed' && onError) {
            onError(data.data);
          }
        }
      } catch (error) {
        console.log('🔌 FRONTEND - Polling failed:', error);
      }
    }, 2000);

    // Cleanup on unmount
    return () => {
      clearInterval(pollInterval);
      if (newSocket) {
        newSocket.emit('unsubscribe-progress', { jobId });
        newSocket.disconnect();
      }
    };
  }, [jobId, onComplete, onError, job]);

  const getStepIcon = (step, status) => {
    if (status === 'completed') {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    } else if (status === 'error') {
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    } else if (step === job?.currentStep && job?.status === 'started') {
      return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
    } else {
      return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStepColor = (step, status) => {
    if (status === 'completed') {
      return 'bg-green-50 border-green-200 text-green-800';
    } else if (status === 'error') {
      return 'bg-red-50 border-red-200 text-red-800';
    } else if (step === job?.currentStep && job?.status === 'started') {
      return 'bg-blue-50 border-blue-200 text-blue-800';
    } else {
      return 'bg-gray-50 border-gray-200 text-gray-600';
    }
  };

  const steps = [
    {
      id: 1,
      name: 'Upload & Process',
      description: 'Uploading resume to database',
      icon: Database
    },
    {
      id: 2,
      name: 'AI Processing',
      description: 'Parsing resume with OpenAI',
      icon: Brain
    },
    {
      id: 3,
      name: 'Parse & Save',
      description: 'Saving parsed data',
      icon: FileText
    }
  ];

  if (!job) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Initializing resume parsing...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Resume Parsing Progress</h2>
                <p className="text-sm text-gray-500">
                  {job.status === 'completed' ? 'Completed' : 
                   job.status === 'failed' ? 'Failed' : 
                   'In Progress'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Connection Status */}
          <div className="flex items-center space-x-2 mb-6">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-600">
              {isConnected ? 'Connected to server' : 'Connecting...'}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Overall Progress</span>
              <span>{Math.round(job.progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div
                className="h-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${job.progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-4">
            {steps.map((step, index) => {
              const stepData = job.steps.find(s => s.id === step.id);
              const status = stepData?.status || 'pending';
              const isActive = step.id === job.currentStep && job.status === 'started';
              const Icon = step.icon;

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    getStepColor(step.id, status)
                  } ${isActive ? 'ring-2 ring-blue-200' : ''}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {getStepIcon(step.id, status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold">{step.name}</h3>
                        {isActive && (
                          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                        )}
                      </div>
                      <p className="text-sm opacity-75">{step.description}</p>
                      {stepData?.message && (
                        <p className="text-xs mt-1 opacity-75">{stepData.message}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <Icon className="w-6 h-6 opacity-50" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Current Message */}
          {job.currentMessage && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center space-x-2">
                <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                <p className="text-sm text-blue-800">{job.currentMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {job.error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <p className="text-sm text-red-800">{job.error}</p>
              </div>
            </div>
          )}

          {/* Parsed Data Preview */}
          {job.status === 'completed' && job.result?.parsedData && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <h3 className="font-semibold text-green-800 mb-2">Parsed Data Preview</h3>
              <div className="text-sm text-green-700">
                <p>✅ Resume successfully parsed with AI</p>
                <p>📊 Extracted: {Object.keys(job.result.parsedData).length} data fields</p>
                <p>💾 Data saved to your profile</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            {job.status === 'completed' && (
              <button
                onClick={onClose}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Continue
              </button>
            )}
            {job.status === 'failed' && (
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            )}
            {job.status === 'started' && (
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Close (Continue in Background)
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ResumeParsingProgress;
